import { useState, useEffect, useCallback } from 'react';
import { getAlternativeNames, updateAlternativeNames } from 'modules/services/api';
import servicesStrings from '../strings';

const useAlternativeNames = (service, isOpen, onClose) => {
  const [alternativeNames, setAlternativeNames] = useState([]);
  const [newAlternativeName, setNewAlternativeName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const resetState = useCallback(() => {
    setAlternativeNames([]);
    setNewAlternativeName('');
    setError('');
    setHasUnsavedChanges(false);
  }, []);

  const loadAlternativeNames = useCallback(async () => {
    if (!service?.serviceKey) return;

    setIsLoading(true);
    setError('');

    try {
      const response = await getAlternativeNames(service.serviceKey);
      if (response.data?.data) {
        setAlternativeNames(response.data.data);
      }
    } catch (err) {
      setError(servicesStrings.alternativeNames.loadError);
      console.error('Error loading alternative names:', err);
    } finally {
      setIsLoading(false);
    }
  }, [service?.serviceKey]);

  const handleModalOpen = useCallback(() => {
    if (service) {
      resetState();
      loadAlternativeNames();
    }
  }, [service, resetState, loadAlternativeNames]);

  const addAlternative = useCallback(() => {
    const trimmedName = newAlternativeName.trim();
    if (!trimmedName) return;

    // Check for duplicates
    if (
      alternativeNames.some(alt => alt.alternativeName.toLowerCase() === trimmedName.toLowerCase())
    ) {
      setError(servicesStrings.alternativeNames.duplicateError);
      return;
    }

    // Clear any previous error
    setError('');

    const newAlternative = {
      alternativeName: trimmedName,
    };

    setAlternativeNames(prev => [...prev, newAlternative]);
    setNewAlternativeName('');
    setHasUnsavedChanges(true);
  }, [newAlternativeName, alternativeNames]);

  const deleteAlternative = useCallback(alternativeName => {
    setAlternativeNames(prev => prev.filter(alt => alt.alternativeName !== alternativeName));
    setHasUnsavedChanges(true);
  }, []);

  const handleUpdateAlternativeName = useCallback(
    (previousName, newName) => {
      const trimmedName = newName.trim();

      // If empty, don't update
      if (!trimmedName) {
        return;
      }

      // Check for duplicates (excluding current item being edited)
      if (
        trimmedName.toLowerCase() !== previousName.toLowerCase() &&
        alternativeNames.some(
          alt => alt.alternativeName.toLowerCase() === trimmedName.toLowerCase(),
        )
      ) {
        setError(servicesStrings.alternativeNames.duplicateError);
        return;
      }

      // If name hasn't changed, don't update
      if (trimmedName === previousName) {
        return;
      }

      // Update the alternative name
      setAlternativeNames(prev =>
        prev.map(alt =>
          alt.alternativeName === previousName ? { alternativeName: trimmedName } : alt,
        ),
      );
      setHasUnsavedChanges(true);
      setError('');
    },
    [alternativeNames],
  );

  // Clear error when user starts typing
  const handleInputChange = useCallback(
    value => {
      setNewAlternativeName(value);
      if (error) {
        setError('');
      }
    },
    [error],
  );

  const saveChanges = useCallback(async () => {
    if (!service?.serviceKey) return;

    setIsSaving(true);
    setError('');

    try {
      await updateAlternativeNames(service.serviceKey, alternativeNames);
      setHasUnsavedChanges(false);
      // Close modal after successful save
      if (onClose) {
        onClose();
      }
    } catch (err) {
      setError(servicesStrings.alternativeNames.saveError);
      console.error('Error saving alternative names:', err);
    } finally {
      setIsSaving(false);
    }
  }, [service?.serviceKey, alternativeNames, onClose]);

  // Load alternative names when modal opens
  useEffect(() => {
    if (isOpen) {
      handleModalOpen();
    }
  }, [isOpen, handleModalOpen]);

  return {
    // State
    alternativeNames,
    newAlternativeName,
    isLoading,
    isSaving,
    error,
    hasUnsavedChanges,

    // Actions
    setNewAlternativeName: handleInputChange,
    addAlternative,
    deleteAlternative,
    saveChanges,
    handleUpdateAlternativeName,
  };
};

export default useAlternativeNames;
